import { useCallback, useEffect, useState } from 'react'
import type { Account, Category, CreditCard, Invoice, Transaction } from '@domain/finance'
import type { ReferenceMonth } from '@shared/date/reference-month'
import type { EnrichmentPatch } from '@domain/ports/finance-repository'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * As duas visões do mês, pelas portas, mais a árvore de categorias — que é o
 * que dá `kind` a cada transação e, com ele, decide o que entra nos totais.
 *
 * **As leituras vão juntas de propósito.** Uma tela que mostrasse os totais
 * antes de as categorias chegarem contaria toda transferência como gasto por um
 * instante, e o número piscaria para baixo quando elas chegassem — um número de
 * dinheiro que muda sozinho na tela é pior do que um que demora.
 *
 * **As faturas e os cartões entram pela mesma porta de entrada, e não por um
 * segundo hook.** Um `useInvoices(month)` à parte daria à tela dois estados de
 * carregamento independentes — e o card de fatura apareceria depois dos totais,
 * empurrando a lista para baixo depois de a pessoa já ter começado a ler. Os
 * cartões vêm junto porque a fatura guarda `creditCardId`, e um card que
 * dissesse o id em vez do nome não seria um card.
 *
 * **As contas entram pelo filtro de instrumento**, que é um seletor de "conta
 * ou cartão" — `source.instrument.id` é um campo só, e o seletor precisa dos
 * dois lados para dar nome a ele.
 */
export type MonthlyBook =
  | { status: 'loading' }
  | { status: 'failed' }
  | {
      status: 'loaded'
      /** *Quanto consumimos neste mês?* Recorta por `transactionDate`. */
      competence: Transaction[]
      /** *Quanto saiu da conta neste mês?* Recorta por `settlementDate`. */
      cash: Transaction[]
      categories: Category[]
      /** As faturas cujo `referenceMonth` é o mês selecionado. */
      invoices: Invoice[]
      /** Todos os cartões, para dar nome ao `creditCardId` da fatura. */
      creditCards: CreditCard[]
      /** Todas as contas, para o seletor de instrumento. */
      accounts: Account[]
      /**
       * Aplica no livro já carregado a edição que acabou de ser gravada.
       *
       * **Existe para não reler o mês inteiro a cada categorização.** A fila de
       * trabalho é: escolhe a categoria, a linha some da lista filtrada,
       * próxima. Reler devolveria a mesma coisa depois de um round-trip, e a
       * lista piscaria entre as duas.
       *
       * Mora aqui, e não numa tabela de correções na tela, porque o `settled`
       * deste hook é carimbado com o mês: trocar de mês substitui o estado
       * inteiro, e uma correção velha não tem como sobreviver à troca. Numa
       * tabela na tela, ela sobreviveria.
       */
      patchEnrichment: (id: string, patch: EnrichmentPatch) => void
      /**
       * Relê o mês inteiro.
       *
       * **Existe para o lançamento manual, e a diferença com `patchEnrichment`
       * é o ponto:** categorizar mexe numa transação que já está na lista, e
       * corrigir no lugar é exato. Uma transação **nova** pode nem pertencer ao
       * mês aberto — quem lança uma despesa de julho estando em agosto não deve
       * vê-la aparecer em agosto. Só relendo é que `isInCompetence` e `isInCash`
       * decidem, que é o trabalho deles; a alternativa seria refazer o recorte
       * aqui, uma terceira vez.
       */
      reload: () => void
    }

/** O resultado **com o mês a que ele pertence**. Ver o comentário abaixo. */
type Settled =
  | { month: ReferenceMonth; status: 'failed' }
  | {
      month: ReferenceMonth
      status: 'loaded'
      competence: Transaction[]
      cash: Transaction[]
      categories: Category[]
      invoices: Invoice[]
      creditCards: CreditCard[]
      accounts: Account[]
    }

export function useMonthlyBook(month: ReferenceMonth): MonthlyBook {
  const {
    transactionRepository,
    categoryRepository,
    invoiceRepository,
    creditCardRepository,
    accountRepository,
  } = useFinanceRepositories()
  const [settled, setSettled] = useState<Settled | null>(null)
  // Um contador, e não um booleano: duas releituras seguidas precisam ser dois
  // valores diferentes, senão a segunda não muda a dependência e o efeito não
  // roda.
  const [geracao, setGeracao] = useState(0)
  const reload = useCallback(() => {
    setGeracao((n) => n + 1)
  }, [])

  useEffect(() => {
    let live = true

    void Promise.all([
      transactionRepository.listByCompetence(month),
      transactionRepository.listByCash(month),
      categoryRepository.list(),
      invoiceRepository.listByMonth(month),
      creditCardRepository.list(),
      accountRepository.list(),
    ])
      .then(([competence, cash, categories, invoices, creditCards, accounts]) => {
        if (live)
          setSettled({
            month,
            status: 'loaded',
            competence,
            cash,
            categories,
            invoices,
            creditCards,
            accounts,
          })
      })
      .catch((error: unknown) => {
        console.error('useMonthlyBook: a leitura do mês falhou', error)
        if (live) setSettled({ month, status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [
    transactionRepository,
    categoryRepository,
    invoiceRepository,
    creditCardRepository,
    accountRepository,
    month,
    geracao,
  ])

  const patchEnrichment = useCallback((id: string, patch: EnrichmentPatch) => {
    setSettled((atual) => {
      if (atual === null || atual.status !== 'loaded') return atual
      const aplicar = (t: Transaction): Transaction =>
        t.id === id
          ? {
              ...t,
              enrichment: {
                ...t.enrichment,
                // Campo ausente é campo não tocado, como no adapter e na dublê.
                // As três implementações dizem a mesma coisa de propósito.
                categoryId:
                  patch.categoryId === undefined ? t.enrichment.categoryId : patch.categoryId,
                note: patch.note === undefined ? t.enrichment.note : patch.note,
                merchantId:
                  patch.merchantId === undefined ? t.enrichment.merchantId : patch.merchantId,
                categorySource:
                  patch.categorySource === undefined
                    ? t.enrichment.categorySource
                    : patch.categorySource,
              },
            }
          : t
      // Nas DUAS listas: uma transação `direct` está nas duas (decisão 96b), e
      // corrigir só uma faria os dois totais discordarem sobre a mesma linha.
      return {
        ...atual,
        competence: atual.competence.map(aplicar),
        cash: atual.cash.map(aplicar),
      }
    })
  }, [])

  // **O mês vem junto do resultado, e a comparação é feita aqui, na
  // renderização.** O caminho óbvio seria um `setSettled(null)` no começo do
  // efeito, e ele tem dois problemas: `react-hooks/set-state-in-effect` o
  // reprova (a mesma regra que `use-members.ts` já contornou), e mesmo passando
  // ele deixaria uma renderização em que o mês do título já é o novo e os
  // números ainda são os do anterior. Com o mês carimbado no resultado, essa
  // renderização não existe: resultado de outro mês **é** carregando.
  if (settled === null || settled.month !== month) return { status: 'loading' }
  return settled.status === 'failed'
    ? { status: 'failed' }
    : {
        status: 'loaded',
        competence: settled.competence,
        cash: settled.cash,
        categories: settled.categories,
        invoices: settled.invoices,
        creditCards: settled.creditCards,
        accounts: settled.accounts,
        patchEnrichment,
        reload,
      }
}

/**
 * Só as duas listas de um mês — sem categorias, faturas, cartões nem contas.
 *
 * Existe para o **mês anterior** do Dashboard, que serve a uma coisa só: a
 * variação percentual. Chamar `useMonthlyBook` duas vezes daria as mesmas cinco
 * leituras em dobro, e quatro delas para nada — a árvore de categorias do mês
 * passado é a mesma de hoje, e fatura, cartão e conta do mês anterior não vão à
 * tela.
 *
 * **E ele não bloqueia a tela.** O Dashboard mostra os números do mês corrente
 * assim que eles chegam; a variação aparece depois, quando este resolve. Um
 * número grande que espera o mês passado para aparecer é um número grande que
 * demora pela razão errada.
 */
export type PreviousMonth =
  | { status: 'loading' | 'failed' }
  | { status: 'loaded'; competence: Transaction[]; cash: Transaction[] }

type SettledPrevious =
  | { month: ReferenceMonth; status: 'failed' }
  | { month: ReferenceMonth; status: 'loaded'; competence: Transaction[]; cash: Transaction[] }

export function useMonthTransactions(month: ReferenceMonth): PreviousMonth {
  const { transactionRepository } = useFinanceRepositories()
  const [settled, setSettled] = useState<SettledPrevious | null>(null)

  useEffect(() => {
    let live = true

    void Promise.all([
      transactionRepository.listByCompetence(month),
      transactionRepository.listByCash(month),
    ])
      .then(([competence, cash]) => {
        if (live) setSettled({ month, status: 'loaded', competence, cash })
      })
      .catch((error: unknown) => {
        console.error('useMonthTransactions: a leitura do mês falhou', error)
        if (live) setSettled({ month, status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [transactionRepository, month])

  // O mês carimbado no resultado, pelo mesmo motivo de `useMonthlyBook`:
  // resultado de outro mês **é** carregando.
  if (settled === null || settled.month !== month) return { status: 'loading' }
  return settled.status === 'failed'
    ? { status: 'failed' }
    : { status: 'loaded', competence: settled.competence, cash: settled.cash }
}
